// Switch a pending laser job to another cut sheet, and copy a cut sheet onto another material (2026-10-21).
// ONE copy, used by the job detail page (pages/JobsDetailsPage.js), the Jobs list's expanded row (pages/JobsPage.js),
// the SO page's Production Detail (pages/SalesOrderPage.jsx) and the Cut Sheet page (pages/CutSheetEntryPage.jsx).
//
// jobq-api "SWITCH A PENDING LASER JOB TO ANOTHER CUT SHEET": GET /tasks/{id}/switch_cut_sheet (whether the
// task may switch + its twin cut sheets), POST /tasks/{id}/switch_cut_sheet (manager PIN + reason; moves the
// task and its job's parts, rewrites both materials' reservations), POST /cut_sheets/{id}/duplicate (the twin
// on another material). The rules live in the API; the UI only offers the button where it could work.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

/** Offer "Switch cut sheet" on this task? A Pending laser task cut from a cut sheet, no sheet recorded, on a
 *  Pending job (jobStatus unknown = let the API decide). */
export function canOfferCutSheetSwitch(task, jobStatus) {
  if (!task || task.workstation !== 'Laser' || task.cut_sheet_id == null) return false;
  if (task.status !== 'Pending') return false;
  if ((Number(task.sheet_count) || 0) > 0) return false;
  return jobStatus == null || jobStatus === 'Pending';
}

/** GET /tasks/{id}/switch_cut_sheet: {task, refusal, candidates}. Throws Error(the API's sentence). */
export async function loadCutSheetSwitchOptions(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/switch_cut_sheet`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** POST /tasks/{id}/switch_cut_sheet. Never throws: {ok, data} or {ok: false, message}. */
export async function switchCutSheet(taskId, { toCutSheetId, fromCutSheetId, reason, pin, recomputeEstimate = false }) {
  try {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/switch_cut_sheet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to_cut_sheet_id: toCutSheetId,
        from_cut_sheet_id: fromCutSheetId ?? null,
        reason,
        pin,
        recompute_estimate: !!recomputeEstimate,
      }),
    });
    if (!res.ok) return { ok: false, status: res.status, message: await apiErrorMessage(res) };
    return { ok: true, data: await res.json() };
  } catch (e) {
    return { ok: false, message: `No answer from JobQ (${e.message}). Reload to see whether it switched.` };
  }
}

/** POST /cut_sheets/{id}/duplicate. Never throws: {ok, data} or {ok: false, message}. */
export async function duplicateCutSheet(cutSheetId, { materialId, name, description = null, notes = null }) {
  try {
    const res = await fetch(`${API_BASE}/cut_sheets/${cutSheetId}/duplicate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ material_id: Number(materialId), cut_sheet_name: name, description, notes }),
    });
    if (!res.ok) return { ok: false, status: res.status, message: await apiErrorMessage(res) };
    return { ok: true, data: await res.json() };
  } catch (e) {
    return { ok: false, message: `No answer from JobQ (${e.message}). Reload the cut sheets to see whether it was made.` };
  }
}

/** 1.7406 -> "1:44" (h:mm), null -> "—". */
export function hoursText(h) {
  if (h == null || Number.isNaN(Number(h))) return '—';
  const mins = Math.round(Number(h) * 60);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
}

/** What a switch did, in one line: "Job #10155 now cuts HR5-…-HR (#1456, 1/2"-CSA36-HR-…) instead of
 *  HR5-… (#1220). Reserved: 1/2"-CSA36-HRPO-… 2 → 1; 1/2"-CSA36-HR-… stays 2." */
export function switchSummary(out) {
  if (!out) return '';
  const moves = (out.reserved || []).map((r) => (r.reserved_before === r.reserved_after
    ? `${r.material_code} stays ${r.reserved_after}`
    : `${r.material_code} ${r.reserved_before ?? '?'} → ${r.reserved_after}`));
  return `Job #${out.master_job_num} now cuts ${out.to?.cut_sheet_name} (#${out.to?.cut_sheet_id}, `
    + `${out.to?.material_code}) instead of ${out.from?.cut_sheet_name} (#${out.from?.cut_sheet_id}).`
    + (moves.length ? ` Reserved: ${moves.join('; ')}.` : '');
}

/** A name for the copy: the old name + the words of the new material's code the old one doesn't have
 *  ("HR5-60X120-FAB31162" on 1/2"-CSA36-HR-60x120-KCMFG instead of …-HRPO-… -> "HR5-60X120-FAB31162-HR"). */
export function suggestDuplicateName(sheetName, oldMaterialCode, newMaterialCode) {
  const base = (sheetName || 'CUT-SHEET').trim();
  const words = (c) => String(c || '').toUpperCase().split(/[^A-Z0-9.]+/).filter(Boolean);
  const old = new Set(words(oldMaterialCode));
  const extra = words(newMaterialCode).filter((w) => !old.has(w));
  return extra.length ? `${base}-${extra.join('-')}` : `${base}-2`;
}

/** The materials a cut sheet can be copied onto: sheet stock, not a remnant, not the sheet's own material;
 *  by default the same thickness and sheet size, `anySize` drops the size, `search` narrows by code /
 *  description. In stock first, then by code. */
export function duplicateMaterialChoices(materials, source, { search = '', anySize = false } = {}) {
  const term = String(search || '').trim().toLowerCase();
  const sameSize = (m) => !source || (Number(m.width) === Number(source.width) && Number(m.length) === Number(source.length));
  return (materials || [])
    .filter((m) => m.material_id !== source?.material_id)
    .filter((m) => (m.material_category || 'Sheet Stock') === 'Sheet Stock' && !m.is_remnant
      && !String(m.material_code || '').startsWith('REM-'))
    .filter((m) => !source?.thickness_id || m.thickness_id === source.thickness_id)
    .filter((m) => anySize || sameSize(m))
    .filter((m) => !term || String(m.material_code || '').toLowerCase().includes(term)
      || String(m.description || '').toLowerCase().includes(term))
    .sort((a, b) => ((Number(b.stock_quantity) > 0) - (Number(a.stock_quantity) > 0))
      || String(a.material_code || '').localeCompare(String(b.material_code || '')));
}
